import React from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2, History, Receipt, Star, QrCode } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { FollowedStoresStrip, FollowStoreButton, useMyFollows } from "@/components/engage/StoreFollow";
import { usePendingReviews } from "@/components/engage/usePendingReviews";
import { useRewardsStreak } from "@/hooks/useRewards";

/**
 * Iter-320 — "My visits" (/my/visits).
 * Customer's engagement session history:
 *   GET /api/classifieds/engagement/sessions/mine?platform=nz_classified
 *   → { sessions: [...], total, limit, offset }
 */

const ENGAGEMENT_HEADERS = { "X-App-Platform": "nz_classified" };
const PAGE_SIZE = 20;

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return `$${v.toFixed(2)}`;
}

function visitDate(v) {
  const raw = v.created_at || v.started_at || v.paid_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
}

/** Iter 775 — the backend now ships a ready-made `resume_link` per row
 *  (null once the review window closes). Prefer it — converted to a
 *  local path so SPA navigation works on any host — and only fall back
 *  to the legacy qr/sid construction for rows without the field. */
function visitResumePath(v) {
  if ("resume_link" in v) {
    if (!v.resume_link) return null;
    try {
      const u = new URL(v.resume_link, window.location.origin);
      return u.pathname + u.search;
    } catch {
      return null;
    }
  }
  const qrId = v.vendor?.qr_code_id;
  return v.status === "payment_only_pending_review_window" && qrId
    ? `/engage/${qrId}?paid=1&sid=${v.id}`
    : null;
}

function StatusPill({ visit }) {
  const s = visit.status || "";
  const pill = (cls, label) => (
    <span className={`rounded-full border text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${cls}`}>{label}</span>
  );
  if (s === "paid") return pill("bg-emerald-50 text-emerald-700 border-emerald-200", "Paid");
  if (s === "resolved_points_credited") return pill("bg-emerald-50 text-emerald-700 border-emerald-200", "Points credited");
  if (s === "verified") return pill("bg-emerald-50 text-emerald-700 border-emerald-200", "Review verified");
  // Iter-329 — the customer HAS paid in this state: show the PAID badge
  // alongside the open review window so the visit never looks unpaid.
  if (s === "payment_only_pending_review_window") {
    return (
      <span className="inline-flex items-center gap-1.5">
        {pill("bg-emerald-50 text-emerald-700 border-emerald-200", "Paid")}
        {pill("bg-amber-50 text-amber-800 border-amber-200", "Review window open")}
      </span>
    );
  }
  if (s === "needs_review") return pill("bg-amber-50 text-amber-800 border-amber-200", "Review pending");
  if (s === "expired_no_review" || s.startsWith("expired")) return pill("bg-slate-100 text-slate-500 border-slate-200", "Expired");
  if (s === "cancelled") return pill("bg-slate-100 text-slate-500 border-slate-200", "Cancelled");
  if (s === "open") return pill("bg-blue-50 text-blue-700 border-blue-200", "Open");
  return null;
}

export default function MyVisitsPage() {
  const { user } = useAuth();

  const q = useInfiniteQuery({
    queryKey: ["my-visits", user?.id],
    enabled: !!user,
    retry: 0,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data } = await sharedApi.get("/classifieds/engagement/sessions/mine", {
        headers: ENGAGEMENT_HEADERS,
        params: { platform: "nz_classified", limit: PAGE_SIZE, offset: pageParam },
      });
      return data;
    },
    getNextPageParam: (last) => {
      const next = (last?.offset || 0) + (last?.sessions?.length || 0);
      return next < (last?.total || 0) ? next : undefined;
    },
  });

  const visits = (q.data?.pages || []).flatMap((p) => p?.sessions || []);
  const totalVisits = q.data?.pages?.[0]?.total ?? visits.length;
  // Iter-336 — cancelled sessions were never paid: exclude their
  // discount from the "Saved" stat too.
  const savedSoFar = visits.reduce(
    (sum, v) => sum + (v.status === "cancelled" ? 0 : Number(v.discount_amount) || 0),
    0,
  );
  const partial = !!q.hasNextPage;
  // Iter-349 row 30 extra — favourite store = most-visited store among
  // loaded sessions (cancelled re-scans excluded so duplicates don't skew it).
  const favouriteStore = React.useMemo(() => {
    const counts = {};
    visits.forEach((v) => {
      if (v.status === "cancelled") return;
      const name = v.vendor?.business_name;
      if (name) counts[name] = (counts[name] || 0) + 1;
    });
    let best = null;
    Object.entries(counts).forEach(([name, n]) => {
      if (!best || n > best.n) best = { name, n };
    });
    return best && best.n > 1 ? best.name : null;
  }, [visits]);

  // Iter-356 — Visit Streaks (display only, no points). Server-canonical
  // badge per store ("🔥 n visits", null under 2 distinct visit days).
  const streakQ = useRewardsStreak(!!user);
  const streakByVendor = {};
  (streakQ.data?.stores || []).forEach((s) => {
    if (s.vendor_id && s.badge) streakByVendor[s.vendor_id] = s.badge;
  });

  // Iter-356 follow-up (founder-approved, display only): confetti when a
  // store crosses a visit milestone. Each store+milestone fires ONCE per
  // browser (localStorage guard); only the highest crossed milestone is
  // celebrated so late-arriving customers don't get stale toasts.
  React.useEffect(() => {
    const stores = streakQ.data?.stores || [];
    if (!stores.length) return;
    const MILESTONES = [5, 10];
    let burst = false;
    stores.forEach((st) => {
      if (!st.vendor_id) return;
      const days = Number(st.visit_days) || 0;
      const crossed = MILESTONES.filter((m) => days >= m);
      if (!crossed.length) return;
      const top = crossed[crossed.length - 1];
      let celebrate = false;
      try {
        crossed.forEach((m) => {
          const key = `allsale.visits.milestone.${st.vendor_id}.${m}`;
          if (!localStorage.getItem(key)) {
            localStorage.setItem(key, "1");
            if (m === top) celebrate = true;
          }
        });
      } catch {
        return; // storage unavailable — skip rather than re-fire forever
      }
      if (celebrate) {
        burst = true;
        toast.success(`${top} visits at ${st.store_name || "your favourite store"} — you're a regular!`);
      }
    });
    if (burst) confetti({ particleCount: 90, spread: 70, origin: { y: 0.3 } });
  }, [streakQ.data]);

  // Store Follow — one follows list powers the strip AND every card's
  // heart state (no per-card status GETs).
  const followsQ = useMyFollows(!!user);
  // Iter-336 — follow/unfollow MUST send the evd_ vendor id (the
  // backend rejects other refs on POST). Sessions expose it top-level
  // as `vendor_id`; follows/mine stores carry the same field.
  const followedVendorIds = new Set(
    (followsQ.data?.stores || []).map((s) => s.vendor_id).filter(Boolean),
  );

  // Iter-329 — deadline + potential points per pending session.
  const pendingQ = usePendingReviews(!!user);
  const pendingBySession = {};
  (pendingQ.data?.pending_reviews || []).forEach((r) => {
    if (r.session_id) pendingBySession[r.session_id] = r;
  });

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="My visits · Allsale Classified" description="Your scan-and-save visit history — discounts earned, bills paid and receipts." />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Account", href: "/account" }, { label: "My visits" }]} />

      <div className="mt-6">
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900" data-testid="my-visits-title">My visits</h1>
        <p className="mt-2 text-slate-500">
          Every store you&apos;ve scanned in at — what you paid, what you saved and your receipts.
        </p>
      </div>

      {q.isLoading && (
        <div className="mt-10 flex items-center justify-center py-16 text-slate-400" data-testid="my-visits-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!q.isLoading && q.isError && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-10 text-center" data-testid="my-visits-error">
          <h2 className="font-display text-lg font-bold text-slate-900">Couldn&apos;t load your visits</h2>
          <p className="mt-2 text-sm text-slate-500">Please try again in a moment.</p>
          <button
            type="button"
            onClick={() => q.refetch()}
            data-testid="my-visits-retry"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold px-5 py-2 transition-colors"
          >
            Try again
          </button>
        </div>
      )}

      {!q.isLoading && !q.isError && visits.length === 0 && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-10 text-center" data-testid="my-visits-empty">
          <History className="w-8 h-8 mx-auto text-slate-300" />
          <h2 className="mt-3 font-display text-lg font-bold text-slate-900">No visits yet</h2>
          <p className="mt-2 text-sm text-slate-500">
            Scan a partner store&apos;s QR code at the till to start saving — your visits will show up here.
          </p>
        </div>
      )}

      {visits.length > 0 && (
        <div className={`mt-8 grid gap-3 ${favouriteStore ? "grid-cols-2 sm:grid-cols-3 max-w-xl" : "grid-cols-2 max-w-sm"}`} data-testid="my-visits-stats">
          <div className="rounded-2xl bg-white shadow-card px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Visits</div>
            <div className="mt-0.5 font-display text-2xl font-black text-slate-900" data-testid="my-visits-stat-count">{totalVisits}</div>
          </div>
          <div className="rounded-2xl bg-white shadow-card px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Saved{partial ? " so far" : ""}
            </div>
            <div className="mt-0.5 font-display text-2xl font-black text-emerald-600" data-testid="my-visits-stat-saved">{money(savedSoFar)}</div>
          </div>
          {favouriteStore && (
            <div className="rounded-2xl bg-white shadow-card px-4 py-3 col-span-2 sm:col-span-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Favourite store</div>
              <div className="mt-0.5 font-display text-base font-black text-slate-900 truncate" data-testid="my-visits-stat-favourite" title={favouriteStore}>{favouriteStore}</div>
            </div>
          )}
        </div>
      )}

      <FollowedStoresStrip followsQ={followsQ} streakByVendor={streakByVendor} />

      <PendingReviewBanner visits={visits} />

      {visits.length > 0 && (
        <ul className="mt-8 space-y-3" data-testid="my-visits-list">
          {visits.map((v) => (
            <VisitCard
              key={v.id}
              visit={v}
              followedVendorIds={followedVendorIds}
              pending={pendingBySession[v.id]}
              streakBadge={streakByVendor[v.vendor_id] || null}
            />
          ))}
        </ul>
      )}

      {q.hasNextPage && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
            data-testid="my-visits-load-more"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-6 py-2.5 transition-colors disabled:opacity-60"
          >
            {q.isFetchingNextPage ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Show more
          </button>
        </div>
      )}
    </div>
  );
}

/* Iter-329 — visits with an open 48h review window get one prominent
   banner up top so the earn-back path is never missed. */
function PendingReviewBanner({ visits }) {
  // Iter 775 — resumable = backend resume_link present (legacy fallback
  // inside visitResumePath); the link opens the EXACT session.
  const pending = visits
    .map((v) => ({ v, path: visitResumePath(v) }))
    .filter((x) => x.path);
  if (pending.length === 0) return null;
  const first = pending[0];
  return (
    <div className="mt-8 rounded-2xl bg-amber-50 border border-amber-200 p-4 md:p-5" data-testid="my-visits-review-banner">
      <div className="flex items-start gap-3">
        <Star className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-slate-900 text-sm">
            {pending.length === 1
              ? "You can still earn your discount back on 1 visit"
              : `You can still earn your discount back on ${pending.length} visits`}
          </div>
          <p className="mt-0.5 text-xs text-slate-600 leading-relaxed">
            Leave the store a quick review within the 48-hour window and your discount gets credited back.
          </p>
        </div>
        <Link
          to={first.path}
          data-testid="my-visits-review-banner-cta"
          className="shrink-0 inline-flex items-center rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-4 py-2 transition-colors"
        >
          Leave a review
        </Link>
      </div>
    </div>
  );
}

function VisitCard({ visit: v, followedVendorIds, pending, streakBadge }) {
  // Iter-320 exact contract: vendor is a nested object; "You saved" is
  // discount_amount; "Paid" is charge_total; receipt_ref is a Stripe
  // payment id rendered as text (no URL exists).
  const vendor = v.vendor || {};
  const store = vendor.business_name || "Store";
  const qrId = vendor.qr_code_id || null;
  const date = visitDate(v);
  const bill = money(v.bill_amount);
  const savedVal = Number(v.discount_amount);
  const saved = Number.isFinite(savedVal) && savedVal > 0 ? money(savedVal) : null;
  const paid = money(v.charge_total);
  // Iter-329 — "PAID $X on {date}" uses the actual payment timestamp.
  const paidDate = v.paid_at
    ? new Date(v.paid_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })
    : null;
  const receiptRef = v.receipt_ref || null;
  // Iter 775 — resume path comes from the backend's resume_link when the
  // field exists (null = window closed), else the legacy construction.
  const resumeTo = visitResumePath(v);
  const reviewOpen = Boolean(resumeTo);
  const deadlineSrc = pending?.review_deadline || v.review_deadline;
  const deadline = deadlineSrc
    ? new Date(deadlineSrc).toLocaleString("en-NZ", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : null;
  const points = pending?.potential_points ?? null;

  return (
    <li className="rounded-2xl bg-white shadow-card p-5" data-testid={`my-visit-${v.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="font-bold text-slate-900">{store}</div>
        {/* Iter-356 — server-canonical streak badge, display only */}
        {streakBadge && (
          <span
            data-testid={`my-visit-streak-${v.id}`}
            className="rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-bold px-2 py-0.5"
          >
            {streakBadge}
          </span>
        )}
        <StatusPill visit={v} />
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-400">
          {v.short_code && <span className="font-bold tracking-widest text-slate-500">{v.short_code}</span>}
          {date && <span>{date}</span>}
        </div>
      </div>

      {/* Iter-336 — cancelled sessions were never paid (replaced by a
          re-scan): no saved/paid amounts for them. */}
      {v.status !== "cancelled" && (bill || saved || paid) && (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {bill && (
            <span className="text-slate-500">Bill <span className="font-semibold text-slate-800">{bill}</span></span>
          )}
          {saved && (
            <span className="text-slate-500">You saved <span className="font-semibold text-emerald-600">{saved}</span></span>
          )}
          {paid && (
            <span className="text-slate-500">
              Paid <span className="font-black text-slate-900">{paid}</span>
              {paidDate && <span className="text-slate-400"> on {paidDate}</span>}
            </span>
          )}
          {Number(v.points_discount_nzd) > 0 && (
            <span className="text-emerald-700 font-semibold" data-testid={`my-visit-points-used-${v.id}`}>
              Points used −{money(v.points_discount_nzd)}
            </span>
          )}
        </div>
      )}

      {/* Iter-329 — loud review-pending badge with deadline + points */}
      {reviewOpen && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid={`my-visit-pending-badge-${v.id}`}>
          <span className="font-bold">Review pending</span>
          {deadline ? ` — post before ${deadline}` : " — the 48-hour window is open"}
          {points != null ? ` to earn ${points} points` : ""}.
        </div>
      )}

      {(receiptRef || reviewOpen || qrId) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs">
          {receiptRef && (
            <span className="inline-flex items-center gap-1.5 text-slate-500" data-testid={`my-visit-receipt-${v.id}`}>
              <Receipt className="w-3.5 h-3.5" /> Receipt {receiptRef}
            </span>
          )}
          {reviewOpen && (
            <Link
              to={resumeTo}
              data-testid={`my-visit-earnback-${v.id}`}
              className="inline-flex items-center gap-1.5 font-semibold text-amber-700 hover:underline"
            >
              <Star className="w-3.5 h-3.5" /> Earn your discount back
            </Link>
          )}
          {!reviewOpen && qrId && (
            <Link
              to={`/engage/${qrId}`}
              data-testid={`my-visit-again-${v.id}`}
              className="inline-flex items-center gap-1.5 font-semibold text-slate-500 hover:text-slate-800"
            >
              <QrCode className="w-3.5 h-3.5" /> Visit again
            </Link>
          )}
          {v.vendor_id && (
            <FollowStoreButton
              vendorRef={v.vendor_id}
              name={store}
              followedIds={followedVendorIds}
              testid={`my-visit-follow-${v.id}`}
            />
          )}
        </div>
      )}
    </li>
  );
}
